import React, { useEffect, useRef, useState } from "react";
import "./SessionGraph.scss";
import { useLocation, useNavigate } from "react-router-dom";
import Spinner from "../../../components/Spinner/Spinner";
import { API_CONSTANTS } from "../../../utils/api-constant";
import { safeParseSessionStorage } from "../../../utils/storage";
import { getLoginType } from "../../../services/auth";
import { useToast } from "../../../utils/ToastContext";
import SessionGraphHeader from "./SessionGraphHeader";
import SessionGraphCharts from "./SessionGraphCharts";

const MEASUREMENT_LABELS = {
  acc: "Accelerometer",
  hr: "Heart Rate",
  temperature: "Temperature",
  eda: "EDA (Electrodermal Activity)",
  spo2estim: "SpO2 (Estimated)",
  calories: "Calories",
  event: "Event",
};

const SessionGraph = () => {
  const token = sessionStorage.getItem("accessToken");
  const [chartsData, setChartsData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const rowData = safeParseSessionStorage("rowData") || {};
  const [commonXAxisCategories, setCommonXAxisCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeRange, setActiveRange] = useState("All");
  const { showToast } = useToast();
  const chartRefs = useRef([]);
  const { referenceData, sessionId, referenceId, isJsonData, selectedDate } =
    location.state || {};

  const fetchData = async (isStale = () => false) => {
    if (!sessionId && !referenceId) {
      console.error("Missing sessionId and referenceId");
      return;
    }

    const url = new URL(API_CONSTANTS.READ_JSON_FILE_FOR_VITAL_VALUES);

    if (isJsonData === true) {
      url.searchParams.append("sessionId", sessionId);
    } else {
      url.searchParams.append("referenceId", referenceId);
    }

    setIsLoading(true);

    try {
      const response = await fetch(url.toString(), {
        method: "POST", // keep POST if backend expects POST
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const responseData = await response.json();
      if (isStale()) return;

      if (responseData?.statusCode === 204) {
        showToast("Data Not Available", "info");
      } else {
        processFetchedData(responseData.data);
      }
    } catch (error) {
      if (isStale()) return;
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;
    if (referenceData) {
      fetchData(() => ignore);
    } else if (!sessionId && !referenceId) {
      // Opened directly or after a refresh: the session to graph was never
      // passed in, so there is nothing to fetch.
      showToast("No session selected. Please go back and select a session.", "info");
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceData]);

  useEffect(() => {
    chartRefs.current = chartRefs.current.slice(0, chartsData.length);
  }, [chartsData]);

  const processFetchedData = (data) => {
    setCommonXAxisCategories(data.map((item) => item.time));
    setIsLoading(false);
    processChartData(data);
  };

  const processChartData = (data) => {
    const measurements = [
      "acc",
      "hr",
      "temperature",
      "eda",
      "spo2estim",
      "calories",
      "event",
    ];
    const processedData = measurements.map((measurement) => {
      const channelData = data.map((item) => ({ y: item[measurement] }));
      return {
        key: measurement,
        name: MEASUREMENT_LABELS[measurement] || measurement.toUpperCase(),
        data: channelData,
        hasData: channelData.some(
          (point) => point.y !== null && point.y !== undefined,
        ),
      };
    });

    const totalStepsData = {
      key: "total_steps",
      name: "Total Steps",
      data: data.map((item) => ({
        y: item.runSteps + item.walkSteps,
      })),
      hasData: data.some(
        (item) =>
          (item.runSteps !== null && item.runSteps !== undefined) ||
          (item.walkSteps !== null && item.walkSteps !== undefined),
      ),
    };

    setChartsData([...processedData, totalStepsData]);
  };

  // The last chart in the array carries the shared navigator/scrollbar/range
  // sync for every stacked chart; if that channel has no data, that shared UI
  // must anchor to the last channel that actually does.
  const getLastVisibleIndex = () =>
    chartsData.reduce(
      (acc, c, i) => (c.hasData ? i : acc),
      chartsData.length - 1,
    );

  const handleRangeSelect = (range) => {
    setActiveRange(range);

    const chart = chartRefs.current[getLastVisibleIndex()];
    if (!chart || !chart.xAxis || !chart.series || !chart.series[0].data) {
      console.error("Chart is not initialized or does not have series data.");
      return;
    }

    const dataLength = chart.series[0].data.length;
    const xAxis = chart.xAxis[0];
    const pointInterval = 1;

    let max = dataLength - 1;
    let min;

    switch (range) {
      case "10s":
        min = Math.max(max - Math.floor(10 / pointInterval), 0);
        break;
      case "30s":
        min = Math.max(max - Math.floor(30 / pointInterval), 0);
        break;
      case "1m":
        min = Math.max(max - Math.floor(60 / pointInterval), 0);
        break;
      case "10m":
        min = Math.max(max - Math.floor((10 * 60) / pointInterval), 0);
        break;
      case "30m":
        min = Math.max(max - Math.floor((30 * 60) / pointInterval), 0);
        break;
      case "1h":
        min = Math.max(max - Math.floor((60 * 60) / pointInterval), 0);
        break;
      case "All":
        min = 0;
        break;
      default:
        console.warn("Unknown range:", range);
        return;
    }

    xAxis.setExtremes(min, max, true, false);
  };

  // Shared by both the HealthCare and SuperAdmin session-graph routes; each
  // role returns to a different place, so branch on role instead of forking
  // this file into two near-identical copies.
  const handlePatientSession = () => {
    if (getLoginType() === "1") {
      if (selectedDate) {
        sessionStorage.setItem("returnSelectedDate", selectedDate);
      }
      navigate(-1);
    } else {
      navigate("/patient-view", { state: { selectedDateReturn: selectedDate } });
    }
  };

  const hasAttemptedFetch = Boolean(sessionId || referenceId);

  return (
    <div>
      {isLoading && (
        <div className="loader-container">
          <div className="loader">
            <Spinner />
          </div>
        </div>
      )}
      <div className="graph">
        <SessionGraphHeader
          title={rowData.name}
          onBack={handlePatientSession}
          activeRange={activeRange}
          onRangeSelect={handleRangeSelect}
        />
        {!isLoading && commonXAxisCategories.length === 0 ? (
          <div className="graph_empty">
            {hasAttemptedFetch
              ? "No vital data available for this session."
              : "No session selected. Please go back and select a session."}
          </div>
        ) : (
          <SessionGraphCharts
            chartsData={chartsData}
            commonXAxisCategories={commonXAxisCategories}
            chartRefs={chartRefs}
            lastVisibleIndex={getLastVisibleIndex()}
          />
        )}
      </div>
    </div>
  );
};

export default SessionGraph;
